import { useRef, useState, useCallback } from 'react';
import { Camera, SwitchCamera } from 'lucide-react';
import { motion } from 'framer-motion';

export function CameraCapture({ onImageCapture }) {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');

  const startCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
      });
      videoRef.current.srcObject = mediaStream;
      setStream(mediaStream);
      setIsActive(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
    }
  }, [facingMode]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      setIsActive(false);
    }
  }, [stream]);

  const toggleCamera = useCallback(() => {
    if (isActive) {
      stopCamera();
    } else {
      startCamera();
    }
  }, [isActive, startCamera, stopCamera]);

  const switchCamera = useCallback(() => {
    stopCamera();
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  }, [stopCamera]);

  const captureImage = useCallback(() => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg');
    onImageCapture(imageData);
    stopCamera();
  }, [onImageCapture, stopCamera]);

  return (
    <div className="relative w-full">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="rounded-lg overflow-hidden bg-black"
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          className={`w-full aspect-[4/3] object-cover ${!isActive ? 'hidden' : ''}`}
        />
        
        {!isActive && (
          <div className="aspect-[4/3] bg-gray-900 flex items-center justify-center">
            <Camera className="w-16 h-16 text-gray-400" />
          </div>
        )}
      </motion.div>

      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-4">
        {isActive && (
          <>
            <motion.button
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              onClick={captureImage}
              className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg"
            >
              <div className="w-12 h-12 bg-emerald-600 rounded-full border-2 border-white" />
            </motion.button>
            
            <motion.button
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              onClick={switchCamera}
              className="w-10 h-10 bg-white/80 rounded-full flex items-center justify-center"
            >
              <SwitchCamera className="w-6 h-6 text-gray-900" />
            </motion.button>
          </>
        )}
      </div>

      <button
        onClick={toggleCamera}
        className="mt-4 w-full py-2 px-4 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
      >
        <Camera className="w-5 h-5" />
        {isActive ? 'Stop Camera' : 'Open Camera'}
      </button>
    </div>
  );
}